/* eslint-disable react/prop-types, react/no-unused-prop-types -- props are typed via TypeScript; the rule cannot resolve imported types or inline column renderers */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import I18n from '@utils/i18n';
import { AssigneeType } from '@enums/assignee-type.enum';
import useGetTechnicians from '@hooks/requests/technicians.get.hook';
import useGetSubcontractors from '@hooks/requests/subcontractors.get.hook';
import useApiQuery from '@hooks/api-params.hook';
import TechnicianSortableAttributes from '@enums/sortable-attributes/technician-sortable-attributes.enum';
import SubcontractorSortableAttributes from '@enums/sortable-attributes/subcontractor-sortable-attributes.enum';
import { Button, ConfirmDialog, InlineNotification, Popup, PopupActionList, PopupSection, SectionHeader, Table, TableSize, Text } from '@ui';
import AdditionalVisitModal from './AdditionalVisitModal';
import { VisitPriority } from './types';
import './AdditionalVisitsStyles.scss';
const FIELD_PREFIX = 'additional_visits';
const NEW_ROW_ID = -1;
const defaultPriorityOptions = () => [
    { value: VisitPriority.NORMAL, label: I18n.t('inspections.form.additional_visits.priorities.normal') },
    { value: VisitPriority.HIGH, label: I18n.t('inspections.form.additional_visits.priorities.high') }
];
function RowActions({ visible, onEdit, onRemove }) {
    const anchorRef = useRef(null);
    const [open, setOpen] = useState(false);
    if (!visible)
        return null;
    const label = I18n.t('inspections.form.additional_visits.row_actions');
    const choose = (action) => () => {
        setOpen(false);
        action();
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(Button, { ref: anchorRef, "aria-haspopup": "menu", "aria-expanded": open, "aria-label": label, title: label, onClick: () => setOpen((prev) => !prev) },
            React.createElement("i", { className: "fa-light fa-ellipsis", "aria-hidden": "true" })),
        React.createElement(Popup, { anchor: anchorRef.current, show: open, onClose: () => setOpen(false), position: "bottom-right" },
            React.createElement(PopupSection, null,
                React.createElement(PopupActionList, null,
                    React.createElement(Button, { role: "menuitem", className: "qmb-ui-popup__action", onClick: choose(onEdit) }, I18n.t('generic.edit')),
                    React.createElement(Button, { role: "menuitem", className: "qmb-ui-popup__action", onClick: choose(onRemove) }, I18n.t('generic.remove')))))));
}
function AdditionalVisits({ initialVisits, buildingId, subcontractorsEnabled, priorityOptions, defaultDuration = 0, canEdit = true }) {
    var _a;
    const keyCounter = React.useRef(0);
    const nextKey = () => {
        keyCounter.current += 1;
        return keyCounter.current;
    };
    const [rows, setRows] = useState(() => initialVisits.map((visit) => (Object.assign(Object.assign({}, visit), { key: nextKey() }))));
    const [editingKey, setEditingKey] = useState(null);
    const [removingKey, setRemovingKey] = useState(null);
    const resolvedPriorityOptions = priorityOptions !== null && priorityOptions !== void 0 ? priorityOptions : defaultPriorityOptions();
    const techniciansQuery = useMemo(() => ({
        select: 'all',
        sort: { attribute: TechnicianSortableAttributes.NAME, direction: 'asc' },
        filter: { forInspections: true, buildingId: buildingId || undefined }
    }), [buildingId]);
    const { apiParams: technicianApiParams } = useApiQuery(techniciansQuery);
    const { data: technicians } = useGetTechnicians(technicianApiParams);
    const subcontractorsQuery = useMemo(() => ({ select: 'all', sort: { attribute: SubcontractorSortableAttributes.NAME, direction: 'asc' } }), []);
    const { apiParams: subcontractorApiParams } = useApiQuery(subcontractorsQuery);
    const { data: subcontractors } = useGetSubcontractors(subcontractorApiParams, subcontractorsEnabled);
    const technicianOptions = useMemo(() => technicians.map((t) => ({ value: t.id, label: t.name })), [technicians]);
    const subcontractorOptions = useMemo(() => subcontractors.map((s) => ({ value: s.id, label: s.name })), [subcontractors]);
    const assigneeName = useCallback((row) => {
        var _a, _b, _c, _d;
        if (row.assigneeType === AssigneeType.SUBCONTRACTOR) {
            return (_b = (_a = subcontractorOptions.find((o) => o.value === row.subcontractorId)) === null || _a === void 0 ? void 0 : _a.label) !== null && _b !== void 0 ? _b : '';
        }
        return (_d = (_c = technicianOptions.find((o) => o.value === row.technicianId)) === null || _c === void 0 ? void 0 : _c.label) !== null && _d !== void 0 ? _d : '';
    }, [technicianOptions, subcontractorOptions]);
    const visibleRows = rows.filter((row) => !row.destroyed);
    const editingRow = (_a = rows.find((row) => row.key === editingKey)) !== null && _a !== void 0 ? _a : null;
    const blankRow = () => ({
        key: NEW_ROW_ID,
        assigneeType: AssigneeType.TECHNICIAN,
        technicianId: null,
        subcontractorId: null,
        scheduledDate: '',
        scheduledTime: '',
        duration: defaultDuration,
        priority: VisitPriority.NORMAL,
        priorityNote: '',
        visitNotes: ''
    });
    const handleSave = (saved) => {
        if (editingKey === NEW_ROW_ID) {
            setRows((prev) => [...prev, Object.assign(Object.assign({}, saved), { key: nextKey() })]);
        }
        else {
            setRows((prev) => prev.map((row) => (row.key === editingKey ? Object.assign(Object.assign(Object.assign({}, row), saved), { key: row.key }) : row)));
        }
        setEditingKey(null);
    };
    const handleConfirmRemove = () => {
        setRows((prev) => prev.flatMap((row) => {
            if (row.key !== removingKey)
                return [row];
            return row.id === undefined ? [] : [Object.assign(Object.assign({}, row), { destroyed: true })];
        }));
        setRemovingKey(null);
    };
    const columns = [
        {
            dataKey: 'scheduledDate',
            label: I18n.t('inspections.form.additional_visits.scheduled_date'),
            columnType: 'primary',
            renderer: ({ item }) => React.createElement(Text, { variant: "body" }, item.scheduledDate)
        },
        {
            dataKey: 'scheduledTime',
            label: I18n.t('inspections.form.additional_visits.scheduled_time'),
            renderer: ({ item }) => React.createElement(Text, { variant: "body" }, item.scheduledTime)
        },
        {
            dataKey: 'assignee',
            label: subcontractorsEnabled
                ? I18n.t('inspections.form.additional_visits.technician_or_subcontractor')
                : I18n.t('inspections.form.additional_visits.technician'),
            renderer: ({ item }) => {
                var _a;
                return (React.createElement("div", null,
                    assigneeName(item), (_a = item.errors) === null || _a === void 0 ? void 0 :
                    _a.map((message) => (React.createElement(InlineNotification, { key: message, variant: "error" }, message)))));
            }
        },
        {
            dataKey: 'duration',
            label: I18n.t('inspections.form.additional_visits.duration'),
            renderer: ({ item }) => { var _a; return React.createElement(Text, { variant: "body" }, (_a = item.duration) !== null && _a !== void 0 ? _a : ''); }
        },
        {
            dataKey: 'priority',
            label: I18n.t('inspections.form.additional_visits.priority'),
            renderer: ({ item }) => {
                var _a, _b;
                return (React.createElement(Text, { variant: "body" }, (_b = (_a = resolvedPriorityOptions.find((o) => o.value === item.priority)) === null || _a === void 0 ? void 0 : _a.label) !== null && _b !== void 0 ? _b : ''));
            }
        },
        {
            dataKey: 'actions',
            label: '',
            width: '1px',
            align: 'right',
            columnType: 'last',
            renderer: ({ item }) => (React.createElement(RowActions, { visible: canEdit, onEdit: () => setEditingKey(item.key), onRemove: () => setRemovingKey(item.key) }))
        }
    ];
    return (React.createElement("div", { className: "inspection-additional-visits" },
        React.createElement(SectionHeader, { title: I18n.t('inspections.form.additional_visits.title'), actions: canEdit && (React.createElement(Button, { variant: "primary", onClick: () => setEditingKey(NEW_ROW_ID) }, I18n.t('inspections.form.additional_visits.add'))) }),
        visibleRows.length > 0 && (React.createElement(Table, { data: visibleRows, columns: columns, variant: "detail", size: TableSize.Full, getItemId: (row) => row.key })),
        rows.map((row, index) => {
            var _a, _b, _c, _d;
            const name = (field) => `${FIELD_PREFIX}[${index}][${field}]`;
            return (React.createElement(React.Fragment, { key: row.key },
                row.id !== undefined && React.createElement("input", { type: "hidden", name: name('id'), value: row.id }),
                row.destroyed ? (React.createElement("input", { type: "hidden", name: name('_destroy'), value: "1" })) : (React.createElement(React.Fragment, null,
                    React.createElement("input", { type: "hidden", name: name('assignee_type'), value: row.assigneeType }),
                    row.assigneeType === AssigneeType.TECHNICIAN && (React.createElement("input", { type: "hidden", name: name('technician_id'), value: (_a = row.technicianId) !== null && _a !== void 0 ? _a : '' })),
                    row.assigneeType === AssigneeType.SUBCONTRACTOR && (React.createElement("input", { type: "hidden", name: name('subcontractor_id'), value: (_b = row.subcontractorId) !== null && _b !== void 0 ? _b : '' })),
                    React.createElement("input", { type: "hidden", name: name('scheduled_date'), value: row.scheduledDate }),
                    React.createElement("input", { type: "hidden", name: name('scheduled_time'), value: row.scheduledTime }),
                    React.createElement("input", { type: "hidden", name: name('duration'), value: (_c = row.duration) !== null && _c !== void 0 ? _c : '' }),
                    React.createElement("input", { type: "hidden", name: name('priority'), value: (_d = row.priority) !== null && _d !== void 0 ? _d : '' }),
                    React.createElement("input", { type: "hidden", name: name('priority_note'), value: row.priorityNote }),
                    React.createElement("input", { type: "hidden", name: name('visit_notes'), value: row.visitNotes })))));
        }),
        editingKey !== null && (React.createElement(AdditionalVisitModal, { isOpen: true, initialRow: editingRow !== null && editingRow !== void 0 ? editingRow : blankRow(), technicianOptions: technicianOptions, subcontractorOptions: subcontractorOptions, subcontractorsEnabled: subcontractorsEnabled, priorityOptions: resolvedPriorityOptions, onSave: handleSave, onClose: () => setEditingKey(null) })),
        React.createElement(ConfirmDialog, { isOpen: removingKey !== null, title: I18n.t('inspections.form.additional_visits.remove_title'), description: I18n.t('inspections.form.additional_visits.remove_description'), confirmLabel: I18n.t('generic.remove'), confirmVariant: "danger", onConfirm: handleConfirmRemove, onCancel: () => setRemovingKey(null) })));
}
export default AdditionalVisits;
